// Derives the maturity level for the 4 controls that Cisco Technology
// Adoption answers are direct evidence for, so the live assess flow never
// asks a separate self-assessment question for them (see
// MaturityStepShared's DERIVED_CONTROL_IDS filter). Test fixtures
// (scoring/__fixtures__/scenarios.ts, scripts/gen-sample-report.ts) bypass
// this — they set MaturityAnswers directly for all 16 controls.
//
// Scoring is authored judgment onto the existing 0-5 evidence scale, not
// derived from an external source:
//   - 3-tier fields (Cisco IQ / Cloud Control hygiene + report utilization):
//     bottom tier -> 1, middle tier -> 2 (hygiene has a 3rd middle rung -> 3),
//     top tier -> 4.
//   - 4-tier per-product utilization fields: bottom -> 1, ad hoc -> 2, top
//     ("process in place...") -> 5, "I use Cisco IQ for this" -> recursively
//     resolves to Cisco IQ's own report_utilization score, not a fixed value.
//   - account_created/org_created = false (or unanswered): every downstream
//     field for that platform scores 1 (they're hidden by the `showIf` gate
//     in the UI, so this is just the default when absent).
//
// Two composite scores, each the MAX across contributing signals (one
// authoritative platform used well is sufficient evidence — tools aren't
// additive requirements):
//   - inventoryVisibilityLevel -> D1.SD1.C1, D2.SD1.C1
//   - reportingRhythmLevel     -> D1.SD3.C1, D2.SD3.C1

import type { TechAdoptionAnswers, UseCaseSelection } from "../shared/types";

type FieldValue = string | boolean | undefined;

function score3Tier(value: FieldValue): number {
  switch (value) {
    case "none":
    case "have_not_viewed":
      return 1;
    case "some":
    case "ad_hoc":
      return 2;
    case "connectors":
      return 3;
    case "full":
    case "process_in_place":
      return 4;
    default:
      return 1;
  }
}

function score4Tier(value: FieldValue): number | "use_iq" {
  switch (value) {
    case "have_not_viewed":
      return 1;
    case "ad_hoc":
      return 2;
    case "process_in_place":
      return 5;
    case "use_iq":
      return "use_iq";
    default:
      return 1;
  }
}

export function deriveAdoptionMaturity(
  techAdoption: TechAdoptionAnswers,
  useCases: UseCaseSelection[],
): Record<string, number> {
  const iq = techAdoption.cisco_iq ?? {};
  const cloudControl = techAdoption.cisco_cloud_control ?? {};

  const iqAdopted = iq.account_created === true;
  const iqHygiene = iqAdopted ? score3Tier(iq.inventory_hygiene) : 1;
  const iqReport = iqAdopted ? score3Tier(iq.report_utilization) : 1;

  const ccAdopted = cloudControl.org_created === true;
  const ccHygiene = ccAdopted ? score3Tier(cloudControl.inventory_hygiene) : 1;
  const ccReport = ccAdopted ? score3Tier(cloudControl.report_utilization) : 1;

  // "I use Cisco IQ for this" defers to Cisco IQ's own report-utilization
  // score — an org that routes through a well-adopted Cisco IQ is exactly as
  // mature as its Cisco IQ practice, no more, no less.
  function resolve4Tier(value: FieldValue): number {
    const s = score4Tier(value);
    return s === "use_iq" ? iqReport : s;
  }

  const selected = new Set(useCases.map((u) => u.id));
  const nexusReport = selected.has("nexus_dc")
    ? resolve4Tier(techAdoption.nexus_dashboard?.report_utilization)
    : undefined;
  const catalystCenterReport = selected.has("catalyst_switching")
    ? resolve4Tier(techAdoption.catalyst_center?.security_advisory_eox_utilization)
    : undefined;
  const merakiReport = selected.has("meraki")
    ? resolve4Tier(techAdoption.meraki?.firmware_inventory_review)
    : undefined;
  const catalystSdwanReport = selected.has("catalyst_sdwan")
    ? resolve4Tier(techAdoption.catalyst_sdwan?.security_advisory_utilization)
    : undefined;
  const firewallsReport = selected.has("cisco_firewall")
    ? resolve4Tier(techAdoption.cisco_firewalls?.software_upgrade_planner_utilization)
    : undefined;
  // Cisco Intersight has no tech-footprint equivalent — always considered.
  const intersightReport = resolve4Tier(techAdoption.cisco_intersight?.security_advisory_utilization);

  const inventoryVisibilityLevel = Math.max(
    iqHygiene,
    ccHygiene,
    nexusReport ?? 1,
    catalystCenterReport ?? 1,
  );

  const reportingRhythmLevel = Math.max(
    iqReport,
    ccReport,
    nexusReport ?? 1,
    merakiReport ?? 1,
    catalystSdwanReport ?? 1,
    catalystCenterReport ?? 1,
    firewallsReport ?? 1,
    intersightReport,
  );

  return {
    "D1.SD1.C1": inventoryVisibilityLevel,
    "D2.SD1.C1": inventoryVisibilityLevel,
    "D1.SD3.C1": reportingRhythmLevel,
    "D2.SD3.C1": reportingRhythmLevel,
  };
}

export const DERIVED_CONTROL_IDS = ["D1.SD1.C1", "D1.SD3.C1", "D2.SD1.C1", "D2.SD3.C1"];
